// ============================================================================
// Hooks TanStack Query — RENCONTRES CG (E3b) : GET /api/meetings + mutations
// ============================================================================
// Hook = TanStack Query SEULEMENT. Autorisation au SERVEUR (viewer.*) ;
// erreurs mutation -> code court ?error= mappe i18n (GA-21).

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, ApiForbiddenError, ApiServerError } from '@gs/api-client';

export type MeetingStatus = 'upcoming' | 'completed' | 'cancelled';

export interface MeetingSummary {
  id: string;
  title: string;
  scheduledAt: string;
  durationMinutes: number | null;
  location: string | null;
  status: MeetingStatus;
  documentCount: number;
}

export interface MeetingsListResponse {
  meetings: MeetingSummary[];
  viewer: { role: 'admin' | 'manager' | 'director' | 'employee'; canCreate: boolean };
}

export interface MeetingDocument {
  id: string;
  fileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedByEmail: string;
  uploadedAt: string;
}

export interface MeetingDetailResponse {
  meeting: {
    id: string;
    title: string;
    scheduledAt: string;
    /** Prefill datetime-local (America/Toronto). */
    scheduledAtLocal: string;
    durationMinutes: number | null;
    location: string | null;
    status: MeetingStatus;
    createdByEmail: string;
    createdAt: string;
  };
  documents: MeetingDocument[];
  viewer: {
    role: 'admin' | 'manager' | 'director' | 'employee';
    email: string;
    canEdit: boolean;
  };
}

export interface MeetingWritePayload {
  title: string;
  /** datetime-local 'YYYY-MM-DDTHH:MM' — le serveur convertit via wallTimeToUtcIso. */
  scheduledAt: string;
  durationMinutes?: number;
  location: string | null;
  status?: MeetingStatus;
}

const LIST_KEY = ['gest', 'meetings'] as const;
const detailKey = (id: string) => ['gest', 'meetings', id] as const;

export function meetingErrorCode(err: unknown): string {
  if (err instanceof ApiForbiddenError) return 'forbidden';
  if (err instanceof ApiServerError) {
    if (err.status === 404) return 'notfound';
    if (err.status === 400) return 'invalid';
  }
  return 'server';
}

export function useMeetings(showAll: boolean, enabled: boolean) {
  return useQuery({
    queryKey: [...LIST_KEY, { showAll }],
    queryFn: () => api.get<MeetingsListResponse>(`/api/meetings${showAll ? '?status=all' : ''}`),
    enabled,
  });
}

export function useMeetingDetail(id: string | undefined, enabled: boolean) {
  return useQuery({
    queryKey: detailKey(id ?? ''),
    queryFn: () => api.get<MeetingDetailResponse>(`/api/meetings/${id}`),
    enabled: enabled && !!id,
    retry: false,
  });
}

function useInvalidateMeetings() {
  const qc = useQueryClient();
  return (id?: string) => {
    void qc.invalidateQueries({ queryKey: LIST_KEY });
    if (id) void qc.invalidateQueries({ queryKey: detailKey(id) });
  };
}

export function useCreateMeeting() {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (payload: MeetingWritePayload) =>
      api.post<{ id: string }>('/api/meetings', {
        ...payload,
        status: payload.status ?? 'upcoming',
      }),
    onSuccess: () => invalidate(),
  });
}

export function useUpdateMeeting(id: string) {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (payload: MeetingWritePayload & { status: MeetingStatus }) =>
      api.put<{ ok: boolean }>(`/api/meetings/${id}`, payload),
    onSuccess: () => invalidate(id),
  });
}

export function useUpdateMeetingStatus(id: string) {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (status: MeetingStatus) =>
      api.patch<{ ok: boolean }>(`/api/meetings/${id}/status`, { status }),
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteMeeting(id: string) {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: () => api.delete<{ ok: boolean }>(`/api/meetings/${id}`),
    onSuccess: () => invalidate(id),
  });
}

export function useUploadMeetingDocument(id: string) {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: async (file: File) => {
      // apiFetch JSON.stringify le body — upload multipart via helper (lot UPLOADS).
      const { uploadGestFile } = await import('../lib/blobs/uploadFile');
      return uploadGestFile<{ id: string }>(`/api/meetings/${id}/documents`, file);
    },
    onSuccess: () => invalidate(id),
  });
}

export function useDeleteMeetingDocument(meetingId: string) {
  const invalidate = useInvalidateMeetings();
  return useMutation({
    mutationFn: (documentId: string) =>
      api.delete<{ ok: boolean }>(`/api/meetings/${meetingId}/documents/${documentId}`),
    onSuccess: () => invalidate(meetingId),
  });
}
